import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { Checkbox, Icon } from "semantic-ui-react";

import { PriceInput, ReactTable } from "components";
import { useShowPriceInVat } from "hooks";
import { parsePriceFromInput } from "lib/Form/components";
import "modules/AppointmentDetails/components/VehicleTyres/components/TyreTable/components/ReplacementOfferModal/components/TyreOfferTable/TyreOfferTable.scss";
import { isCarReadyOrQualityCheckDoneOrCanceled } from "utils/appointmentUtils";
import { getTyreSeasonIcon } from "utils/common";

import type { Appointment } from "models";
import type { ReplacementOffer } from "modules/AppointmentDetails/components/VehicleTyres/components/TyreTable/components/ReplacementOfferModal/hooks";

type TyreOfferTableProps = {
  replacementOffers: ReplacementOffer[];
  appointment: Appointment;
  canUpdateAppointments: boolean;
  getDisplayedOfferPrice: (price: number) => number;
  onSelectionUpdate: (offer: ReplacementOffer, selected: boolean) => void;
  onPriceUpdate: (offer: ReplacementOffer, price: number) => void;
};

const columnHelper = createColumnHelper<ReplacementOffer>();

export const TyreOfferTable = ({
  replacementOffers,
  appointment,
  canUpdateAppointments,
  getDisplayedOfferPrice,
  onSelectionUpdate,
  onPriceUpdate
}: TyreOfferTableProps) => {
  const { t } = useTranslation();

  const showPriceInVat = useShowPriceInVat();
  const isAppointmentDisabled = !canUpdateAppointments || isCarReadyOrQualityCheckDoneOrCanceled(appointment.appointment_status_identifier);

  const columns = [
    columnHelper.accessor(row => row, {
      id: "season",
      header: "",
      size: 45,
      cell: ({ row: { original } }) => <Icon className={getTyreSeasonIcon(original.season)} />
    }),
    columnHelper.accessor(row => row, {
      id: "description",
      header: () => t("v8_description", "Description"),
      size: 330,
      cell: ({ row: { original } }) => original.description
    }),
    columnHelper.accessor(row => row, {
      id: "brand",
      header: () => t("v8_brand", "Brand"),
      size: 120,
      cell: ({ row: { original } }) => original.manufacturer
    }),
    columnHelper.accessor(row => row, {
      id: "price",
      header: () => (showPriceInVat ? t("v8_price_inc_vat", "Price (incl. VAT)") : t("v8_price_excl_vat", "Price (excl. VAT)")),
      size: 150,
      cell: ({ row: { original } }) => (
        <PriceInput
          key={`${original.id}-${showPriceInVat}`}
          defaultValue={getDisplayedOfferPrice(original.price)}
          placeholder="0,00"
          style={{ maxWidth: "80px" }}
          onBlur={(event: React.FocusEvent<HTMLInputElement>) => onPriceUpdate(original, parsePriceFromInput(event.target.value))}
          disabled={isAppointmentDisabled || !original.selected}
        />
      )
    }),
    columnHelper.accessor(row => row, {
      id: "add",
      header: () => <div className="th-add">{t("v8_add", "Add")}</div>,
      size: 75,
      cell: ({ row: { original } }) => (
        <Checkbox className="check" checked={original.selected} onChange={(_e, data) => onSelectionUpdate(original, data.checked ?? false)} />
      )
    })
  ];

  return (
    <div className="TyreOfferTable">
      <ReactTable data={replacementOffers} columns={columns} />
    </div>
  );
};
